import React, { useState, useEffect } from 'react'
import TailwindDataTable from '../../../components/TailwindDataTable'
import ReactPaginate from 'react-paginate'
import PageSizeSelectBox from '../../../components/PageSizeSelectBox'
import { FaCheckDouble, FaPlus, FaEdit, FaTrash, FaEye } from 'react-icons/fa'
// import { BiImport } from 'react-icons/bi'
import { IoMdSearch } from 'react-icons/io'
import { useTranslation } from 'react-i18next'
import Select from "react-select";
import { FaArrowRightLong } from 'react-icons/fa6'
import { ApiDelete } from '../../../helper/axiosFunctions'
import { parseListResponse, searchStudents } from '../../../helper/adminUsersApi'
import EditStudentModel from '../../../components/model/EditStudentModel'
import ViewStudentModel from '../../../components/model/ViewStudentModel'
import DeleteConfirmationModel from '../../../components/model/DeleteConfirmationModel'
import { commonToast } from '../../../helper/Toast'
import AddStudentModel from '../../../components/model/AddStudentModel'
// import ImportStudentCSVModel from '../../../components/model/ImportStudentCSVModel'
import { getSelectedSchoolId } from '../../../helper/commonFunctions'

export default function UserList() {
    const { t } = useTranslation()
    const [currentPage, setCurrentPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [searchQuery, setSearchQuery] = useState('')
    const [filterRole, setFilterRole] = useState('all')
    const [limit, setLimit] = useState(10)
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(false)
    const [totalPages, setTotalPages] = useState(0)

    const [isEditOpen, setIsEditOpen] = useState(false)
    const [editingStudent, setEditingStudent] = useState(null)
    
    const [isViewOpen, setIsViewOpen] = useState(false)
    const [viewingStudent, setViewingStudent] = useState(null)

    const [isDeleteOpen, setIsDeleteOpen] = useState(false)
    const [deletingStudent, setDeletingStudent] = useState(null)
    
    const [isAddOpen, setIsAddOpen] = useState(false)
    // const [isImportCSVOpen, setIsImportCSVOpen] = useState(false)
    const schoolId = getSelectedSchoolId();

    const fetchUsers = async () => {
        try {
            setLoading(true)
            const response = await searchStudents({
                offset: currentPage,
                limit,
                schoolId,
            });
            const list = parseListResponse(response);
            setUsers(list);
            if (list.length < limit) {
                setTotalPages(currentPage + 1);
            } else {
                setTotalPages(currentPage + 2);
            }
        } catch (error) {
            console.error('Error fetching users:', error)
            setTotalPages(currentPage > 0 ? currentPage : 1);
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!deletingStudent) return;
        try {
            await ApiDelete(`/admins/students/${deletingStudent.studentId}`);
            commonToast(t('student_deleted_successfully') || 'Student deleted successfully!', 'success');
            fetchUsers(); // Refresh list
        } catch (error) {
            commonToast(error?.data?.error || error?.message || 'Failed to delete student', 'error');
        }
    };

    useEffect(() => {
        fetchUsers()
    }, [currentPage, limit])

    const columns = [
        {
            key: 'oneTimeKey',
            label: 'Onetime Key',
            formatter: (cell, row) => {
                const oneTimeKey = row.oneTimeKey || row['oneTime key'] || cell;
                return oneTimeKey || "N/A";
            }
        },
        {
            key: 'username',
            label: t('user_name'),
            formatter: (cell, row) => {
                return (
                    <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-500 text-sm font-medium">
                                    {row.firstName ? row.firstName[0] : '?'}
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{row.firstName || "-"}</div>
                            <div className="text-sm text-gray-500">{row.email || "-"}</div>
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'lastName',
            label: t('last_name'),
            formatter: (cell) => cell || "-"
        },
        {
            key: 'email',
            label: t('email'),
            formatter: (cell) => cell || "-"
        },
        {
            key: 'userRoles',
            label: t('role'),
            formatter: (cell) => {
                if (!cell?.length) return "-";
                return cell.map((role, index) => (
                    <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1">
                        {role.replace('zubrus_', '')}
                    </span>
                ));
            }
        },
        {
            key: 'actions',
            label: t('actions'),
            formatter: (_, row) => {
                return (
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => {
                                setViewingStudent(row)
                                setIsViewOpen(true)
                            }}
                            className="text-green-600 hover:text-green-800 transition-colors duration-200"
                            title={t('view')}
                        >
                            <FaEye size={18} />
                        </button>
                        <button
                            className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                            title={t('edit')}
                            onClick={() => {
                                setEditingStudent(row)
                                setIsEditOpen(true)
                            }}
                        >
                            <FaEdit size={18} />
                        </button>
                        <button
                            className="text-red-600 hover:text-red-800 transition-colors duration-200"
                            title={t('remove')}
                            onClick={() => {
                                setDeletingStudent(row);
                                setIsDeleteOpen(true);
                            }}
                        >
                            <FaTrash size={18} />
                        </button>
                    </div>
                );
            }
        }
    ]

    const handlePageChange = (selectedItem) => {
        setCurrentPage(selectedItem.selected);
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(0); // Reset to first page when searching
    };

    const handleRoleFilter = (selectedOption) => {
        setFilterRole(selectedOption.value);
        setCurrentPage(0); // Reset to first page when filtering
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = (user.firstName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
            (user.email?.toLowerCase() || '').includes(searchQuery.toLowerCase());
        const matchesRole = filterRole === 'all' || user.userRoles?.includes(filterRole);
        const isVisible = user.status !== 'DELETED';
        return matchesSearch && matchesRole && isVisible;
    });

    const roleOptions = [
        { value: 'all', label: t('all') },
        { value: 'zubrus_student', label: 'Student' },
        { value: 'zubrus_teacher', label: 'Teacher' },
        { value: 'zubrus_admin', label: 'Admin' },
        { value: 'zubrus_content_creator', label: 'Content Creator' },
        { value: 'zubrus_user', label: 'User' }
    ];

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow p-6">
                {/* Header */}
                <div className="flex flex-wrap justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">{t('user_management.title')}</h1>
                    <div className="flex gap-4 md:mt-4 sm:flex-col">
                        {/* <button
                            className="primary-btn"
                            onClick={() => setIsImportCSVOpen(true)}
                        >
                            <BiImport size={20} />
                            {t('user_management.import_csv')}
                        </button> */}
                        <button 
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            onClick={() => setIsAddOpen(true)}
                        >
                            <FaPlus size={20} />
                            {t('user_management.add_user')}
                        </button>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={t('user_management.search_placeholder')}
                                className="w-1/2 md:w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={searchQuery}
                                onChange={handleSearch}
                            />
                            <IoMdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        </div>
                    </div>
                    <div className="min-w-[200px]">
                        <Select
                            options={roleOptions}
                            placeholder={t('user_management.filter_by_role')}
                            onChange={handleRoleFilter}
                            defaultValue={roleOptions[0]}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                </div>

                {/* Data Table */}
                <>
                    <TailwindDataTable
                        columns={columns}
                        data={filteredUsers}
                        thClasses='text-white'
                        loading={loading}
                    />

                    {/* Pagination Controls */}
                    <div className="flex justify-between items-center mt-4">
                        <div className='xs:mb-5'>
                            <PageSizeSelectBox selected={limit} setSelected={setLimit} setPage={setCurrentPage} />
                        </div>
                        <div className='flex gap-4'>
                            <ReactPaginate
                                breakLabel="..."
                                nextLabel=">"
                                onPageChange={handlePageChange}
                                pageRangeDisplayed={5}
                                pageCount={totalPages}
                                previousLabel="<"
                                renderOnZeroPageCount={null}
                                containerClassName="flex gap-2"
                                pageClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                activeClassName="bg-blue-500 text-white"
                                previousClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                nextClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                disabledClassName="opacity-50 cursor-not-allowed"
                            />
                        </div>
                    </div>
                </>
                {/* Edit Student Modal */}
                <EditStudentModel
                    open={isEditOpen}
                    setOpen={setIsEditOpen}
                    student={editingStudent}
                    onUpdated={fetchUsers}
                />
                {/* View Student Modal */}
                <ViewStudentModel
                    open={isViewOpen}
                    setOpen={setIsViewOpen}
                    student={viewingStudent}
                />
                {/* Delete Student Modal */}
                <DeleteConfirmationModel
                    open={isDeleteOpen}
                    setOpen={setIsDeleteOpen}
                    onConfirm={handleDelete}
                    title={t('delete_student_title')}
                    message={t('delete_student_message')}
                />
                {/* Add Student Modal */}
                <AddStudentModel
                    open={isAddOpen}
                    setOpen={setIsAddOpen}
                    onCreated={fetchUsers}
                    schoolId={schoolId}
                />
                {/* Import CSV Modal
                <ImportStudentCSVModel
                    open={isImportCSVOpen}
                    setOpen={setIsImportCSVOpen}
                    onImported={fetchUsers}
                    schoolId={schoolId}
                /> */}
            </div>
        </div>
    )
}
